import { useEffect, useState } from "react";
import { useCategories } from "./useCategories";
import { useSubscriptionTags } from "./useSubscriptionTags";
import { type Subscription } from "@/types/subscription";

interface UseTagInitializationProps {
  subscription?: Subscription;
}

export const useTagInitialization = ({
  subscription,
}: UseTagInitializationProps) => {
  const { categories } = useCategories();
  const { tagsBySubscriptionId } = useSubscriptionTags();
  const [tagIds, setTagIds] = useState<string[]>(
    subscription ? tagsBySubscriptionId[subscription.id] || [] : [],
  );

  useEffect(() => {
    if (!subscription?.id) return;

    // Prefer tags from the join table (authoritative)
    const fromJoin = tagsBySubscriptionId[subscription.id] || [];
    if (fromJoin.length > 0) {
      setTagIds((prev) => {
        const same =
          prev.length === fromJoin.length &&
          prev.every((v, i) => v === fromJoin[i]);
        return same ? prev : fromJoin;
      });
      return;
    }

    // Fallback: derive from legacy category name (for backward compatibility)
    if (subscription.category && categories.length > 0) {
      const match = categories.find(
        (c) => c.name.toLowerCase() === subscription.category!.toLowerCase(),
      );
      if (match) {
        setTagIds((prev) => (prev.length === 0 ? [match.id] : prev));
      }
    }
  }, [
    subscription?.id,
    subscription?.category,
    tagsBySubscriptionId,
    categories,
  ]);

  return {
    tagIds,
    setTagIds,
  };
};
